import React, { useEffect, useState } from 'react';
import { X, Save, MessageCircle, Plus, Trash2, Tag, Clock, ArrowRight, Edit, Globe, Megaphone, MapPin, Mail, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { QUALIFICATION_CONFIG, SOURCE_CONFIG, formatDate, getSourceLabel, estadoInfo, estadosPara, MOTIVOS_PERDIDA, LINEAS, DECISOR, esProspecto, TIPOS_MAIL, RESPUESTAS_MAIL } from '../utils/constants';
import WhatsAppModal from './WhatsAppModal';
import AiAnalysis from './AiAnalysis';
import { useAuth } from '../context/AuthContext';
import { hasFeature, clienteTiene } from '../utils/features';
import CamposPropios, { leerCampos, valorDe, mostrar } from './CamposPropios';

/* Una línea del historial. Los tres tipos se leen distinto: los cambios de
   estado y de calificación son un "de esto a esto", y la edición de datos es
   la lista de campos que se tocaron en ese guardado. */
function Movimiento({ mov, source }) {
  const flecha = <ArrowRight size={12} color="var(--text-muted)" />;

  if (mov.tipo === 'calificacion') {
    const pinta = (v) => {
      const c = QUALIFICATION_CONFIG[v];
      return <span className="badge" style={{ background: 'var(--bg-input)', color: c?.color, fontSize: 10 }}>{c ? `${c.icon} ${c.label}` : v || '—'}</span>;
    };
    return <>{pinta(mov.previous_status)}{flecha}{pinta(mov.new_status)}</>;
  }

  if (mov.tipo === 'datos') {
    return (
      <span style={{ color: 'var(--text-muted)' }}>
        <Edit size={11} style={{ verticalAlign: 'middle', marginRight: 5 }} />
        Editó <span style={{ color: 'var(--text-primary)' }}>{mov.detalle}</span>
      </span>
    );
  }

  // 'estado', y también lo viejo, que no tiene tipo
  const pinta = (v) => (
    <span className="badge" style={{ background: estadoInfo(v, source).bg, color: estadoInfo(v, source).color, fontSize: 10 }}>
      {estadoInfo(v, source).label}
    </span>
  );
  return <>{pinta(mov.previous_status)}{flecha}{pinta(mov.new_status)}</>;
}

export default function LeadDetail({ leadId, onClose }) {
  const [lead, setLead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState('');
  const [waOpen, setWaOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const { tenant, user } = useAuth();
  const canWhatsApp = hasFeature(tenant, user, 'lead_whatsapp');
  const canAnalysis = hasFeature(tenant, user, 'ai_analysis') && hasFeature(tenant, user, 'inbox');
  // "Ubicación" en lugar de "Ciudad": solo en el CRM que lo pidió (Punto Reciclaje)
  const conUbicacion = clienteTiene(tenant, 'ubicacion_lead');
  const loadConvs = () => { if (canAnalysis) api.get(`/conversations/by-lead/${leadId}`).then(r => setConvs(r.data)).catch(() => {}); };
  useEffect(() => { loadConvs(); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => { api.get(`/leads/${leadId}`).then(res => { setLead(res.data); setForm(res.data); }); };
  useEffect(() => { load(); }, [leadId]);

  /* Los campos que definió este cliente para sus leads. Se leen de su
     configuración, así el mismo CRM sirve para una organizadora de eventos y
     para cualquier otro negocio sin tocar la tabla. */
  const [campos, setCampos] = useState([]);
  const [propios, setPropios] = useState({});
  const [webmail, setWebmail] = useState('');
  useEffect(() => { api.get('/settings').then(r => { setCampos(leerCampos(r.data)); setWebmail(r.data?.webmail_prospeccion_mail || ''); }).catch(() => {}); }, []);

  // La charla de mails de la prospección por mail (la registra el agente)
  const [mails, setMails] = useState([]);
  const [mailAbierto, setMailAbierto] = useState(null);
  const esMail = lead?.source === 'prospeccion_mail';
  useEffect(() => { if (esMail) api.get(`/leads/${leadId}/mails`).then(r => setMails(r.data)).catch(() => {}); }, [leadId, esMail]);
  useEffect(() => {
    if (!lead) return;
    const v = {};
    campos.forEach(c => { v[c.key] = valorDe(lead, c.key); });
    setPropios(v);
  }, [lead, campos]);
  if (!lead) return null;

  const saveEdit = async () => {
    try { await api.put(`/leads/${leadId}`, { ...form, extra_data: propios }); toast.success('Lead actualizado'); setEditing(false); load(); }
    catch { toast.error('Error al guardar'); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try { await api.post(`/leads/${leadId}/notes`, { content: newNote }); setNewNote(''); load(); }
    catch { toast.error('Error'); }
  };

  const deleteNote = async (noteId) => { try { await api.delete(`/leads/${leadId}/notes/${noteId}`); load(); } catch { toast.error('Error'); } };
  const addTag = async () => {
    if (!newTag.trim()) return;
    try { await api.post(`/leads/${leadId}/tags`, { tag_name: newTag }); setNewTag(''); load(); }
    catch { toast.error('Error'); }
  };
  const removeTag = async (tagId) => { try { await api.delete(`/leads/${leadId}/tags/${tagId}`); load(); } catch { toast.error('Error'); } };
  /* Al marcar Ganado se pregunta por cuánto.

     Es el único dato del circuito de Google Ads que no sale solo, y es el que
     hace que las campañas busquen clientes grandes en vez de muchos chicos: sin
     monto, una venta de diez mil pesos pesa igual que una de medio millón. Se
     pregunta acá, que es cuando se sabe; si no lo sabés todavía, se deja vacío
     y se completa después desde la ficha. */
  const updateStatus = async (status) => {
    const datos = { status };
    if (status === 'ganado' && status !== lead.status) {
      const r = window.prompt('¿Por cuánto se cerró? (en pesos, sin puntos)\n\nSe lo mandamos a Google Ads para que busque clientes parecidos.\nDejalo vacío si todavía no lo sabés.', lead.monto_ganado || '');
      if (r === null) return;                       // canceló: no se cambia el estado
      if (r.trim()) datos.monto = r.replace(/[^\d]/g, '');
    }
    try { await api.put(`/leads/${leadId}/status`, datos); load(); } catch { toast.error('Error'); }
  };

  const guardarMotivo = async (motivo) => {
    try { await api.put(`/leads/${leadId}/motivo-perdida`, { motivo }); load(); } catch { toast.error('Error'); }
  };
  const guardarDato = async (campo, valor) => {
    try { await api.put(`/leads/${leadId}/dato-prospecto`, { campo, valor }); load(); } catch { toast.error('Error'); }
  };

  const editarMonto = async () => {
    const r = window.prompt('Monto de la venta (en pesos, sin puntos)', lead.monto_ganado || '');
    if (r === null) return;
    try {
      await api.put(`/leads/${leadId}/status`, { status: lead.status, monto: r.replace(/[^\d]/g, '') });
      toast.success('Monto actualizado'); load();
    } catch { toast.error('Error'); }
  };
  const updateQual = async (q) => { try { await api.put(`/leads/${leadId}/qualification`, { qualification: q }); load(); } catch { toast.error('Error'); } };
  const deleteLead = async () => {
    if (!window.confirm(`¿Eliminar "${lead.business_name || 'este lead'}"?`)) return;
    try { await api.delete(`/leads/${leadId}`); toast.success('Lead eliminado'); onClose(); }
    catch { toast.error('Error'); }
  };

  const editFields = [
    { key: 'business_name', label: 'Nombre del negocio' },
    { key: 'phone', label: 'Teléfono' },
    { key: 'email', label: 'Email' },
    { key: 'website', label: 'Sitio web' },
    { key: 'address', label: 'Dirección' },
    { key: 'city', label: conUbicacion ? 'Ubicación' : 'Ciudad' },
    { key: 'category', label: 'Sector' },
    { key: 'service_interest', label: 'Servicio de interés' },
    { key: 'rating', label: 'Rating' },
    { key: 'inquiry', label: 'Consulta / Mensaje' },
    { key: 'utm_source', label: 'UTM Source' },
    { key: 'utm_medium', label: 'UTM Medium' },
    { key: 'utm_campaign', label: 'UTM Campaign' },
    { key: 'utm_content', label: 'UTM Content' },
    { key: 'utm_term', label: 'UTM Term' },
    { key: 'landing_page', label: 'Landing Page' },
    { key: 'referrer_url', label: 'Referrer' },
    { key: 'gclid', label: 'GCLID' },
    { key: 'fbclid', label: 'FBCLID' },
  ];

  const infoFields = [
    ['Teléfono', lead.phone], ['Email', lead.email], ['Web', lead.website],
    ['Dirección', lead.address], [conUbicacion ? 'Ubicación' : 'Ciudad', lead.city], ['Sector', lead.category],
    ['Rating', lead.rating], ['Base', lead.dataset_name]
  ];

  const trackingFields = [
    ['Fuente', getSourceLabel(lead)], ['UTM Source', lead.utm_source], ['UTM Medium', lead.utm_medium],
    ['Campaña', lead.utm_campaign], ['UTM Content', lead.utm_content],
    ['Landing Page', lead.landing_page], ['Referrer', lead.referrer_url],
    ['Servicio', lead.service_interest], ['Consulta', lead.inquiry],
    ['GCLID', lead.gclid], ['FBCLID', lead.fbclid]
  ];

  return (
    <>
      <div className="sidebar-overlay" onClick={onClose} />
      <div className="sidebar-panel">
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>{lead.business_name || 'Lead'}</h2>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-ghost" onClick={deleteLead} title="Eliminar lead"><Trash2 size={16} color="var(--danger)" /></button>
            <button className="btn btn-ghost" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        <div style={{ padding: 24 }}>
          {/* Status & Qualification */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            <select value={lead.status} onChange={e => updateStatus(e.target.value)}
              style={{ background: estadoInfo(lead.status, lead.source).bg, color: estadoInfo(lead.status, lead.source).color, border: 'none', borderRadius: 50, padding: '6px 14px', fontSize: 13, fontWeight: 600, width: 'auto' }}>
              {estadosPara(lead.source, lead.status).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            {/* Un prospecto perdido dice por qué: es lo que se cuenta en el análisis semanal */}
            {esProspecto(lead.source) && lead.status === 'perdido' && (
              <select value={valorDe(lead, 'motivo_perdida')} onChange={e => guardarMotivo(e.target.value)}
                title="Motivo de pérdida"
                style={{ background: 'var(--bg-input)', border: '1px solid ' + (valorDe(lead, 'motivo_perdida') ? 'var(--border)' : '#ef4444'), borderRadius: 50, padding: '6px 14px', fontSize: 13, fontWeight: 600, width: 'auto' }}>
                <option value="">¿Por qué se perdió?</option>
                {Object.entries(MOTIVOS_PERDIDA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            )}
            {/* Desde qué línea se le escribió: en la agencia, en todos los leads (Emanuel usa
                dos números). Si llegó al que decide es solo de la prospección por WhatsApp. */}
            {(lead.source === 'prospeccion' || /^agencia\./.test(window.location.hostname)) && (
              <select value={valorDe(lead, 'linea') || ''} onChange={e => guardarDato('linea', e.target.value)} title="Línea de WhatsApp desde la que se le escribió"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 50, padding: '6px 14px', fontSize: 13, fontWeight: 600, width: 'auto' }}>
                <option value="">Línea: sin dato</option>
                {Object.entries(LINEAS).map(([k, v]) => <option key={k} value={k}>Línea {v.label}</option>)}
              </select>
            )}
            {lead.source === 'prospeccion' && (
              <select value={valorDe(lead, 'decisor') || ''} onChange={e => guardarDato('decisor', e.target.value)} title="¿Habla con el dueño o con quien decide?"
                style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 50, padding: '6px 14px', fontSize: 13, fontWeight: 600, width: 'auto' }}>
                <option value="">Habla con el dueño: sin dato</option>
                {Object.entries(DECISOR).map(([k, v]) => <option key={k} value={k}>Habla con el dueño: {v}</option>)}
              </select>
            )}
            <select value={lead.qualification} onChange={e => updateQual(e.target.value)}
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 50, padding: '6px 14px', fontSize: 13, fontWeight: 600, width: 'auto' }}>
              {Object.entries(QUALIFICATION_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
            <span className="badge" style={{ background: SOURCE_CONFIG[lead.source]?.bg || 'rgba(100,116,139,0.15)', color: SOURCE_CONFIG[lead.source]?.color || '#64748b' }}>
              {getSourceLabel(lead)}
            </span>
            {/* Los mismos accesos que en la fila de la lista: la web y la ficha de Maps del prospecto */}
            {esProspecto(lead.source) && lead.website && (
              <a className="badge" href={/^https?:\/\//i.test(lead.website) ? lead.website : 'https://' + lead.website}
                target="_blank" rel="noopener noreferrer" title="Abrir su web"
                style={{ background: 'var(--accent-glow)', color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
                <Globe size={12} /> Web
              </a>
            )}
            {esProspecto(lead.source) && /^https?:\/\//i.test(String(valorDe(lead, 'maps') || '')) && (
              <a className="badge" href={valorDe(lead, 'maps')} target="_blank" rel="noopener noreferrer" title="Abrir en Google Maps"
                style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
                <MapPin size={12} /> Maps
              </a>
            )}
            {/* Lo que se vendió, solo cuando hay algo que mostrar */}
            {lead.status === 'ganado' && (
              <button className="badge" onClick={editarMonto}
                title="Es lo que Google Ads usa para buscar clientes parecidos"
                style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
                {lead.monto_ganado
                  ? '$' + Number(lead.monto_ganado).toLocaleString('es-AR', { maximumFractionDigits: 0 })
                  : 'Cargar el monto'}
              </button>
            )}
            {/* De qué campaña vino, si entró con código de origen */}
            {lead.origen_campana && (
              <span className="badge" title={`Código ${lead.origen_codigo}${lead.origen_ref_id ? ' · visita ' + lead.origen_ref_id : ''}`}
                style={{ background: 'var(--bg-input)', color: 'var(--text-secondary)' }}>
                {lead.origen_campana}
              </span>
            )}
          </div>

          {/* Contact Info */}
          <div className="card" style={{ marginBottom: 16 }}>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {editFields.map(f => (
                  <div key={f.key} className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: 11 }}>{f.label}</label>
                    <input value={form[f.key] || ''} onChange={e => setForm({...form, [f.key]: e.target.value})} style={{ padding: '6px 10px', fontSize: 13 }} />
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveEdit}><Save size={14} /> Guardar</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancelar</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Información</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)} style={{ fontSize: 12, gap: 4 }}><Edit size={12} /> Editar</button>
                </div>
                {infoFields.map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13, minWidth: 0 }}>
                    <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
                    {label === 'Web' && val ? (
                      <a href={/^https?:\/\//i.test(val) ? val : 'https://' + val} target="_blank" rel="noopener noreferrer" title={val}
                        style={{ fontWeight: 500, color: 'var(--accent)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {String(val).replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '')}
                      </a>
                    ) : (
                      <span title={val || ''} style={{ fontWeight: 500, color: val ? 'var(--text-primary)' : 'var(--text-muted)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>{val || '—'}</span>
                    )}
                  </div>
                ))}
                {/* Los datos propios del negocio, junto al resto de la ficha */}
                {campos.map(c => (
                  <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13, minWidth: 0 }}>
                    <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{c.label || c.key}</span>
                    <span style={{ fontWeight: 500, color: valorDe(lead, c.key) ? 'var(--text-primary)' : 'var(--text-muted)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {mostrar(c, valorDe(lead, c.key))}
                    </span>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>Creado: {formatDate(lead.created_at)}</div>
              </div>
            )}
          </div>

          {/* Lo que dejó Prospección: el problema que vio y el primer mensaje armado */}
          {esProspecto(lead.source) && (valorDe(lead, 'observacion') || valorDe(lead, 'mensaje_whatsapp')) && (
            <div className="card" style={{ marginBottom: 16, fontSize: 13 }}>
              {valorDe(lead, 'observacion') && (
                <div style={{ marginBottom: valorDe(lead, 'mensaje_whatsapp') ? 12 : 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 4 }}>OBSERVACIÓN</div>
                  {valorDe(lead, 'observacion')}
                </div>
              )}
              {valorDe(lead, 'mensaje_whatsapp') && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 4 }}>MENSAJE DE WHATSAPP PREPARADO</div>
                  <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>{valorDe(lead, 'mensaje_whatsapp')}</div>
                </div>
              )}
            </div>
          )}

          {/* Prospección por mail: en qué paso de la secuencia va, cómo respondió y la charla entera */}
          {esMail && (
            <div className="card" style={{ marginBottom: 16, fontSize: 13 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <Mail size={14} color="#0891b2" />
                <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Prospección por mail</span>
                {webmail && (
                  <a className="btn btn-secondary btn-sm" href={webmail} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto' }}>
                    <Mail size={12} /> Abrir casilla <ExternalLink size={11} />
                  </a>
                )}
              </div>
              {[
                ['Mail', lead.email ? <a href={`mailto:${lead.email}`}>{lead.email}</a> : '—'],
                ['Tipo', TIPOS_MAIL[valorDe(lead, 'tipo')] || '—'],
                ['Plataforma', valorDe(lead, 'plataforma') || '—'],
                ['Secuencia', `${Number(valorDe(lead, 'paso') || 0)} de 3 mails enviados`],
                ['Último envío', valorDe(lead, 'ultimo_envio') ? formatDate(String(valorDe(lead, 'ultimo_envio')).replace(' ', 'T') + 'Z') : '—'],
                ['Próximo envío', lead.status === 'baja' ? 'No se le escribe más' : (valorDe(lead, 'proximo_envio') || '—')],
                ['Respuesta', RESPUESTAS_MAIL[valorDe(lead, 'respuesta')] ? <span style={{ color: RESPUESTAS_MAIL[valorDe(lead, 'respuesta')].color, fontWeight: 600 }}>{RESPUESTAS_MAIL[valorDe(lead, 'respuesta')].label}</span> : '—'],
                ...(valorDe(lead, 'calificado_at') ? [['Calificado', formatDate(String(valorDe(lead, 'calificado_at')).replace(' ', 'T') + 'Z')]] : []),
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{k}</span><span style={{ textAlign: 'right' }}>{v}</span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, margin: '14px 0 6px' }}>MAILS ({mails.length})</div>
              {mails.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Todavía no hay mails registrados.</div>}
              {mails.map(m => (
                <div key={m.id} onClick={() => setMailAbierto(mailAbierto === m.id ? null : m.id)}
                  style={{ padding: '8px 10px', marginBottom: 6, borderRadius: 8, cursor: 'pointer', background: m.direccion === 'recibido' ? 'rgba(22,163,74,0.08)' : 'var(--bg-input)', borderLeft: '3px solid ' + (m.direccion === 'recibido' ? '#16a34a' : '#0891b2') }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <b>{m.direccion === 'recibido' ? '↘ Respondió' : `↗ Enviado${m.paso ? ` · mail ${m.paso}` : ''}`}</b>
                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formatDate(m.fecha)}</span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{m.asunto || '(sin asunto)'}</div>
                  {mailAbierto === m.id && (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                      <div style={{ color: 'var(--text-muted)' }}>De: {m.de || '—'} · Para: {m.para || '—'}</div>
                      <div style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{m.cuerpo || ''}</div>
                      {m.enlace && <a href={m.enlace} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>Abrir el hilo en la casilla <ExternalLink size={11} /></a>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* En modo edición, los campos propios con su control según el tipo */}
          {editing && (
            <CamposPropios campos={campos} valores={propios}
              onCambio={(k, v) => setPropios(p => ({ ...p, [k]: v }))} />
          )}

          {/* Tracking Data */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Megaphone size={14} color="var(--accent)" />
              <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Datos de tracking</span>
            </div>
            {trackingFields.map(([label, val]) => {
              const isUrl = typeof val === 'string' && /^https?:\/\//i.test(val);
              return (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
                  {isUrl ? (
                    <a href={val} target="_blank" rel="noopener noreferrer" title={val} style={{ fontWeight: 500, color: 'var(--accent)', minWidth: 0, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>{val.replace(/^https?:\/\/(www\.)?/i, '')}</a>
                  ) : (
                    <span style={{ fontWeight: 500, color: val ? 'var(--text-primary)' : 'var(--text-muted)', maxWidth: 260, wordBreak: 'break-all', textAlign: 'right' }}>{val || '—'}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* WhatsApp */}
          {canWhatsApp && lead.phone && (
            <button className="btn btn-sm" onClick={() => setWaOpen(true)}
              style={{ background: '#22c55e', color: '#fff', width: '100%', justifyContent: 'center', marginBottom: 16 }}>
              <MessageCircle size={16} /> Contactar por WhatsApp
            </button>
          )}

          {/* Tags */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>{clienteTiene(tenant, 'etiquetas_charla') ? 'Etiquetas' : 'Tags'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {lead.tags?.map(tag => (
                <span key={tag.id} className="badge" style={{ background: 'var(--accent-glow)', color: 'var(--accent)' }}>
                  <Tag size={10} /> {tag.name}
                  <button onClick={() => removeTag(tag.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 0 }}><X size={10} /></button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Nuevo tag" style={{ padding: '4px 10px', fontSize: 12, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addTag()} />
              <button className="btn btn-secondary btn-sm" onClick={addTag}><Plus size={12} /></button>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Notas</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Agregar nota..." style={{ flex: 1, padding: '6px 10px', fontSize: 13 }} onKeyDown={e => e.key === 'Enter' && addNote()} />
              <button className="btn btn-primary btn-sm" onClick={addNote}><Plus size={14} /></button>
            </div>
            {lead.notes?.map(note => (
              <div key={note.id} style={{ padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formatDate(note.created_at)}</span>
                  <button onClick={() => deleteNote(note.id)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={12} color="var(--text-muted)" /></button>
                </div>
                <p style={{ marginTop: 4 }}>{note.content}</p>
              </div>
            ))}
          </div>

          {/* Historial de movimientos: estado, calificación y ediciones */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>
              <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Historial de movimientos
            </div>
            {lead.history?.length > 0 ? lead.history.map(h => (
              <div key={h.id} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, padding: '7px 0', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
                <Movimiento mov={h} source={lead.source} />
                <span style={{ color: 'var(--text-muted)', marginLeft: 'auto', fontSize: 11, textAlign: 'right' }}>
                  {h.quien && <span style={{ marginRight: 6 }}>{h.quien}</span>}
                  {formatDate(h.changed_at)}
                </span>
              </div>
            )) : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sin movimientos</p>}
          </div>

          {/* Análisis IA de la conversación */}
          {canAnalysis && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Conclusión del agente IA</div>
              {convs.length > 0
                ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{convs.map(c => (
                    <div key={c.id}>
                      {convs.length > 1 && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{c.channel === 'instagram' ? 'Instagram' : 'WhatsApp'}</div>}
                      <AiAnalysis conv={c} onUpdated={loadConvs} />
                    </div>
                  ))}</div>
                : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Este lead no tiene conversaciones con el agente.</p>}
            </div>
          )}

          {/* Contact Log */}
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>
              <MessageCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Historial de contactos
            </div>
            {lead.contacts?.length > 0 ? lead.contacts.map(c => (
              <div key={c.id} style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8, marginBottom: 6, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#22c55e', fontWeight: 600 }}>WhatsApp</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formatDate(c.contacted_at)}</span>
                </div>
                {c.template_name && <div style={{ color: 'var(--text-muted)' }}>Plantilla: {c.template_name}</div>}
              </div>
            )) : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sin contactos</p>}
          </div>
        </div>
      </div>
      {waOpen && <WhatsAppModal lead={lead} onClose={() => { setWaOpen(false); load(); }} />}
    </>
  );
}
